import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, User, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CLAIM_TYPES = [
  'Health cashless', 'Health reimbursement',
  'Motor own damage', 'Motor third party',
  'Life or death', 'Other',
];
export const CLAIM_STATUSES = [
  'Intimated', 'Documents pending', 'Submitted to insurer',
  'Under review', 'Query raised', 'Settled', 'Rejected', 'Closed',
];

const emptyForm = {
  client_id: '',
  policy_id: '',
  claim_number: '',
  claim_type: '',
  status: 'Intimated',
  date_of_loss: '',
  date_reported: '',
  insurer_claim_ref: '',
  claimed_amount: '',
  description: '',
  next_followup_date: '',
  settled_amount: '',
  settled_date: '',
  rejection_reason: '',
};

/**
 * AddClaimDialog — create or edit a claim.
 * Props:
 *   open, onClose, onSaved(claim)
 *   editing: existing claim object OR null for create
 *   presetClientId: optional — pre-select this client (e.g. launched from client page)
 */
const AddClaimDialog = ({ open, onClose, onSaved, editing = null, presetClientId = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPolicies, setClientPolicies] = useState([]);
  const [saving, setSaving] = useState(false);

  // Hydrate when dialog opens.
  useEffect(() => {
    if (!open) return;
    setClientQuery('');
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(({ data }) => setClients(data || []))
      .catch(() => setClients([]));
  }, [open]);

  // Preload the editing row or preset client.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyForm,
        ...Object.fromEntries(Object.entries(editing).map(([k, v]) => [k, v == null ? '' : v])),
      });
      setSelectedClient({ client_id: editing.client_id, name: editing.client_name, primary_mobile: editing.client_mobile });
    } else {
      setForm({ ...emptyForm, client_id: presetClientId || '' });
      setSelectedClient(null);
    }
  }, [open, editing, presetClientId]);

  // When presetClientId arrives and clients loaded, auto-select.
  useEffect(() => {
    if (presetClientId && clients.length && !selectedClient) {
      const c = clients.find((x) => x.client_id === presetClientId);
      if (c) setSelectedClient(c);
    }
  }, [presetClientId, clients, selectedClient]);

  // Load policies when client changes.
  useEffect(() => {
    if (!selectedClient) { setClientPolicies([]); return; }
    axios.get(`${API}/policies?client_id=${selectedClient.client_id}`, { withCredentials: true })
      .then(({ data }) => setClientPolicies(data || []))
      .catch(() => setClientPolicies([]));
    setForm((f) => ({ ...f, client_id: selectedClient.client_id }));
  }, [selectedClient]);

  const filteredClients = useMemo(() => {
    if (!clientQuery.trim()) return clients.slice(0, 20);
    const q = clientQuery.toLowerCase();
    return clients.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.primary_mobile || '').includes(q) ||
      (c.customer_code || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [clients, clientQuery]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.client_id) { toast.error('Pick a client'); return; }
    if (!form.claim_type) { toast.error('Choose claim type'); return; }
    if (!form.date_of_loss) { toast.error('Date of loss is required'); return; }
    if (form.status === 'Settled') {
      if (!form.settled_amount || !form.settled_date) {
        toast.error('Settled amount and date are required'); return;
      }
    }
    if (form.status === 'Rejected' && !(form.rejection_reason || '').trim()) {
      toast.error('Rejection reason is required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        policy_id: form.policy_id || null,
        claim_number: form.claim_number || null,
        date_reported: form.date_reported || null,
        insurer_claim_ref: form.insurer_claim_ref || null,
        claimed_amount: form.claimed_amount === '' ? null : parseFloat(form.claimed_amount),
        description: form.description || null,
        next_followup_date: form.next_followup_date || null,
        settled_amount: form.settled_amount === '' ? null : parseFloat(form.settled_amount),
        settled_date: form.settled_date || null,
        rejection_reason: form.rejection_reason || null,
      };
      let res;
      if (editing) {
        res = await axios.put(`${API}/claims/${editing.claim_id}`, payload, { withCredentials: true });
        toast.success('Claim updated');
      } else {
        res = await axios.post(`${API}/claims`, payload, { withCredentials: true });
        toast.success(`Claim ${res.data?.claim_number || 'created'}`);
      }
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Claim' : 'New Claim'}</DialogTitle>
          <DialogDescription>
            {editing ? 'Update details, status, or follow-up.' : 'Pick the client, optionally the policy, then fill in the loss details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Client picker (searchable) */}
          <div>
            <Label className="text-xs">Client *</Label>
            {selectedClient ? (
              <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2" data-testid="claim-client-selected">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-700" />
                  <span className="text-sm font-medium text-slate-900">{selectedClient.name}</span>
                  <span className="text-xs text-slate-500">{selectedClient.primary_mobile || ''}</span>
                </div>
                {!editing && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedClient(null); setForm((f) => ({ ...f, client_id: '', policy_id: '' })); }} data-testid="claim-change-client">
                    Change
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="h-4 w-4 text-slate-400 absolute left-2 top-2.5" />
                  <Input className="pl-8" placeholder="Search by name, mobile, or customer code"
                    value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} data-testid="claim-client-search" />
                </div>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                  {filteredClients.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No matches.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {filteredClients.map((c) => (
                        <li key={c.client_id}>
                          <button type="button" onClick={() => setSelectedClient(c)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                            data-testid={`claim-client-row-${c.client_id}`}>
                            <span className="font-medium text-slate-900">{c.name}</span>
                            <span className="text-xs text-slate-500">{c.primary_mobile || c.customer_code || '—'}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Policy (optional) */}
          {selectedClient && (
            <div>
              <Label className="text-xs">Linked Policy (optional)</Label>
              <Select value={form.policy_id || '__none__'} onValueChange={(v) => update({ policy_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="claim-policy-select">
                  <SelectValue placeholder="Pick a policy or leave blank" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {clientPolicies.map((p) => (
                    <SelectItem key={p.policy_id} value={p.policy_id} data-testid={`claim-policy-option-${p.policy_id}`}>
                      <span className="inline-flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5" />
                        {p.policy_number} · {p.insurer} · {p.product_name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clientPolicies.length === 0 && (
                <p className="text-[11px] text-slate-500 mt-1">This client has no policies on record — you can file the claim without one.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Claim Number</Label>
              <Input value={form.claim_number} onChange={(e) => update({ claim_number: e.target.value })}
                placeholder="Auto-generated if left blank" data-testid="claim-number-input" />
            </div>
            <div>
              <Label className="text-xs">Claim Type *</Label>
              <Select value={form.claim_type} onValueChange={(v) => update({ claim_type: v })}>
                <SelectTrigger data-testid="claim-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {CLAIM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => update({ status: v })}>
                <SelectTrigger data-testid="claim-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLAIM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Insurer Claim Ref #</Label>
              <Input value={form.insurer_claim_ref} onChange={(e) => update({ insurer_claim_ref: e.target.value })}
                placeholder="If assigned by insurer" data-testid="claim-insurer-ref" />
            </div>

            <div>
              <Label className="text-xs">Date of Loss *</Label>
              <Input type="date" value={form.date_of_loss} onChange={(e) => update({ date_of_loss: e.target.value })} data-testid="claim-date-of-loss" />
            </div>
            <div>
              <Label className="text-xs">Date Reported to Insurer</Label>
              <Input type="date" value={form.date_reported} onChange={(e) => update({ date_reported: e.target.value })} data-testid="claim-date-reported" />
            </div>

            <div>
              <Label className="text-xs">Claimed Amount (₹)</Label>
              <Input type="number" min="0" value={form.claimed_amount} onChange={(e) => update({ claimed_amount: e.target.value })} data-testid="claim-claimed-amount" />
            </div>
            <div>
              <Label className="text-xs">Next Follow-up Date</Label>
              <Input type="date" value={form.next_followup_date} onChange={(e) => update({ next_followup_date: e.target.value })} data-testid="claim-followup-date" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Short Description</Label>
            <Input value={form.description} onChange={(e) => update({ description: e.target.value })}
              placeholder="What happened — one line" data-testid="claim-description" />
          </div>

          {/* Conditional: Settled */}
          {form.status === 'Settled' && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="claim-settled-fields">
              <div>
                <Label className="text-xs">Settled Amount (₹) *</Label>
                <Input type="number" min="0" value={form.settled_amount} onChange={(e) => update({ settled_amount: e.target.value })} data-testid="claim-settled-amount" />
              </div>
              <div>
                <Label className="text-xs">Settled Date *</Label>
                <Input type="date" value={form.settled_date} onChange={(e) => update({ settled_date: e.target.value })} data-testid="claim-settled-date" />
              </div>
            </div>
          )}

          {/* Conditional: Rejected */}
          {form.status === 'Rejected' && (
            <div className="rounded-lg border border-red-200 bg-red-50/60 p-3" data-testid="claim-rejected-fields">
              <Label className="text-xs">Rejection Reason *</Label>
              <Input value={form.rejection_reason} onChange={(e) => update({ rejection_reason: e.target.value })}
                placeholder="Why the insurer rejected the claim" data-testid="claim-rejection-reason" />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="claim-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="claim-save">
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Claim'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddClaimDialog;
